// Main app component with routing and providers

import { lazy, Suspense } from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'sonner';
import { queryClient } from '@/lib/queryClient';
import { persister, CACHE_BUSTER } from '@/lib/queryPersister';
import { AuthProvider } from '@/hooks/useAuth';
import { useDisableZoom } from '@/hooks/useDisableZoom';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AppErrorFallback } from '@/components/AppErrorFallback';
import { Layout } from '@/components/Layout';
import { FullScreenFallback } from '@/components/RouteFallback';
import { InstallPrompt } from '@/components/InstallPrompt';
import { UpdatePrompt } from '@/components/UpdatePrompt';
import { AppLockGate } from '@/components/AppLockGate';
import { FinancialLockGate } from '@/features/lock/FinancialLockGate';

// Route-level code splitting (issue #396): every page is a lazy chunk so the
// initial bundle ships only the app shell. Pages inside <Layout> resolve
// against the Suspense boundary around its <Outlet>; the auth pages and the
// backstage subtree resolve against the top-level boundary below.
const LoginPage = lazy(() =>
  import('@/features/auth/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const SignupPage = lazy(() =>
  import('@/features/auth/SignupPage').then((m) => ({ default: m.SignupPage }))
);
const VerifyEmailPage = lazy(() =>
  import('@/features/auth/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage }))
);
const InviteAcceptPage = lazy(() =>
  import('@/features/auth/InviteAcceptPage').then((m) => ({ default: m.InviteAcceptPage }))
);
const CategoriesPage = lazy(() =>
  import('@/features/categories/CategoriesPage').then((m) => ({ default: m.CategoriesPage }))
);
const AccountsPage = lazy(() =>
  import('@/features/accounts/AccountsPage').then((m) => ({ default: m.AccountsPage }))
);
const BillsPage = lazy(() =>
  import('@/features/bills/BillsPage').then((m) => ({ default: m.BillsPage }))
);
const ExpensesPage = lazy(() =>
  import('@/features/expenses/ExpensesPage').then((m) => ({ default: m.ExpensesPage }))
);
const DashboardPage = lazy(() =>
  import('@/features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage }))
);
const HubPage = lazy(() =>
  import('@/features/hub/HubPage').then((m) => ({ default: m.HubPage }))
);
const SettingsPage = lazy(() =>
  import('@/features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);
const ReportsPage = lazy(() =>
  import('@/features/reports/ReportsPage').then((m) => ({ default: m.ReportsPage }))
);
const InvestmentsPage = lazy(() =>
  import('@/features/investments/InvestmentsPage').then((m) => ({ default: m.InvestmentsPage }))
);
const SearchExpensesPage = lazy(() =>
  import('@/features/expenses/SearchExpensesPage').then((m) => ({ default: m.SearchExpensesPage }))
);
const ListsPage = lazy(() =>
  import('@/features/lists/ListsPage').then((m) => ({ default: m.ListsPage }))
);
const AgendaPage = lazy(() =>
  import('@/features/agenda/AgendaPage').then((m) => ({ default: m.AgendaPage }))
);
const OAuthConsentPage = lazy(() =>
  import('@/features/oauth/OAuthConsentPage').then((m) => ({ default: m.OAuthConsentPage }))
);

// All backstage pages come from one barrel so they share a single admin chunk.
const backstageRoutes = () => import('@/features/backstage/routes');
const BackstageLayout = lazy(() =>
  backstageRoutes().then((m) => ({ default: m.BackstageLayout }))
);
const BackstageDashboard = lazy(() =>
  backstageRoutes().then((m) => ({ default: m.BackstageDashboard }))
);
const BackstageUsers = lazy(() =>
  backstageRoutes().then((m) => ({ default: m.BackstageUsers }))
);
const BackstageHouseholds = lazy(() =>
  backstageRoutes().then((m) => ({ default: m.BackstageHouseholds }))
);

export function App() {
  // Lock the app at 1x scale — blocks desktop keyboard/wheel zoom (issue #346).
  useDisableZoom();

  return (
    <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 24 * 60 * 60 * 1000,
        buster: CACHE_BUSTER,
        // Persist paused (offline-queued) mutations only; see Task 5 for the
        // Lists mutationKey convention. Persist all queries (offline reads).
        dehydrateOptions: {
          shouldDehydrateMutation: (m) => m.state.isPaused,
        },
      }}
      onSuccess={() => {
        // After the cache restores (incl. after an offline reload), replay any
        // mutations that were queued while offline.
        queryClient.resumePausedMutations();
      }}
    >
      <BrowserRouter>
        <AuthProvider>
          <AppLockGate>
            <Suspense fallback={<FullScreenFallback />}>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/invite/:token" element={<InviteAcceptPage />} />
                <Route element={<ProtectedRoute />}>
                  <Route element={<Layout />}>
                    {/* Always accessible — no financial data */}
                    <Route path="/" element={<HubPage />} />
                    <Route path="/lists" element={<ListsPage />} />
                    <Route path="/agenda" element={<AgendaPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    {/* Financial routes — gated by the PIN lock while engaged */}
                    <Route element={<FinancialLockGate />}>
                      <Route path="/finance" element={<DashboardPage />} />
                      <Route path="/expenses" element={<ExpensesPage />} />
                      <Route path="/bills" element={<BillsPage />} />
                      <Route path="/reports" element={<ReportsPage />} />
                      <Route path="/investments" element={<InvestmentsPage />} />
                      <Route path="/search" element={<SearchExpensesPage />} />
                      <Route path="/categories" element={<CategoriesPage />} />
                      <Route path="/accounts" element={<AccountsPage />} />
                    </Route>
                  </Route>
                  <Route path="/oauth/consent" element={<OAuthConsentPage />} />
                  <Route path="/backstage" element={<BackstageLayout />}>
                    <Route index element={<BackstageDashboard />} />
                    <Route path="users" element={<BackstageUsers />} />
                    <Route path="households" element={<BackstageHouseholds />} />
                  </Route>
                </Route>
              </Routes>
            </Suspense>
          </AppLockGate>
        </AuthProvider>
        <InstallPrompt />
        <UpdatePrompt />
      </BrowserRouter>
      {/* Top-right keeps toasts away from primary actions (dialog confirms,
          quick-add, bottom nav) that live near the bottom-right (#472). The
          top offset clears the fixed app header (safe-area inset + ~3.25rem
          row), whose top-right corner holds the user menu. */}
      <Toaster
        theme="dark"
        position="top-right"
        richColors
        offset={{ top: 'calc(env(safe-area-inset-top) + 4rem)' }}
        mobileOffset={{ top: 'calc(env(safe-area-inset-top) + 4rem)' }}
      />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </PersistQueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}
